import { useMemo, useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FixedSizeGrid as Grid, type GridChildComponentProps } from 'react-window';
import { getAllSkins, getFavorites, addFavoriteApi, removeFavoriteApi } from '../api';
import { SkinCard } from '../components/SkinCard';
import { PageHeader, Skeleton } from '../components/ui';
import { useApp } from '../stores/app';
import type { WeaponSkin } from '@vst/shared';
import { SKIN_TIERS } from '@vst/shared';

export function AllSkinsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { favorites, setFavorites, toggleFavoriteLocal } = useApp();

  const skinsQuery = useQuery({
    queryKey: ['skins'],
    queryFn: getAllSkins,
    staleTime: 1000 * 60 * 60, // 1h
  });

  const favQuery = useQuery({ queryKey: ['favorites'], queryFn: getFavorites });
  useEffect(() => {
    if (favQuery.data) setFavorites(new Set(favQuery.data.map((f) => f.skinUuid)));
  }, [favQuery.data, setFavorites]);

  const [search, setSearch] = useState('');
  const [weapon, setWeapon] = useState('');
  const [tier, setTier] = useState('');

  const allSkins = skinsQuery.data?.skins ?? [];

  // Уникальные оружия для фильтра
  const weapons = useMemo(() => {
    const s = new Set<string>();
    for (const sk of allSkins) if (sk.weaponName) s.add(sk.weaponName);
    return [...s].sort();
  }, [allSkins]);

  const filtered = useMemo(() => {
    return allSkins.filter((s) => {
      // Исключаем скины без отображаемой иконки или "Random Favorite Skin"
      if (!s.displayIcon && !s.wallpaper) return false;
      if (search && !s.displayName.toLowerCase().includes(search.toLowerCase())) return false;
      if (weapon && s.weaponName !== weapon) return false;
      if (tier && s.contentTierUuid !== tier) return false;
      return true;
    });
  }, [allSkins, search, weapon, tier]);

  const toggleFav = async (uuid: string) => {
    const isFav = favorites.has(uuid);
    toggleFavoriteLocal(uuid);
    try {
      if (isFav) await removeFavoriteApi(uuid);
      else await addFavoriteApi(uuid);
    } catch {
      toggleFavoriteLocal(uuid);
    } finally {
      void qc.invalidateQueries({ queryKey: ['favorites'] });
    }
  };

  return (
    <div className="pt-6">
      <PageHeader title={t('skins.title')} />

      {/* Search */}
      <input
        type="search"
        placeholder={t('skins.search')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full clip-corners-sm bg-valo-navy-soft border border-valo-navy-border px-3 py-2.5 text-valo-white mb-3 focus:outline-none focus:border-valo-red"
      />

      {/* Filters */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <select
          value={weapon}
          onChange={(e) => setWeapon(e.target.value)}
          className="clip-corners-sm bg-valo-navy-soft border border-valo-navy-border px-3 py-2 text-valo-white focus:outline-none focus:border-valo-red text-sm"
        >
          <option value="">{t('skins.anyWeapon')}</option>
          {weapons.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          className="clip-corners-sm bg-valo-navy-soft border border-valo-navy-border px-3 py-2 text-valo-white focus:outline-none focus:border-valo-red text-sm"
        >
          <option value="">{t('skins.anyTier')}</option>
          <option value={SKIN_TIERS.SELECT}>Select</option>
          <option value={SKIN_TIERS.DELUXE}>Deluxe</option>
          <option value={SKIN_TIERS.PREMIUM}>Premium</option>
          <option value={SKIN_TIERS.EXCLUSIVE}>Exclusive</option>
          <option value={SKIN_TIERS.ULTRA}>Ultra</option>
        </select>
      </div>

      {/* Counter */}
      <div className="text-xs text-valo-muted mb-2 font-mono">
        {filtered.length} / {allSkins.length}
      </div>

      {/* Virtualized grid */}
      {skinsQuery.isLoading ? (
        <div className="grid grid-cols-2 gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[5/2]" />
          ))}
        </div>
      ) : (
        <VirtualSkinGrid
          skins={filtered}
          favorites={favorites}
          onToggle={toggleFav}
        />
      )}
    </div>
  );
}

// ============================================================
// Виртуализованная сетка
// ============================================================
interface VGridProps {
  skins: WeaponSkin[];
  favorites: Set<string>;
  onToggle: (uuid: string) => void;
}

function VirtualSkinGrid({ skins, favorites, onToggle }: VGridProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) {
        setSize({
          width: rect.width,
          height: Math.max(window.innerHeight - rect.top - 80, 400),
        });
      }
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const COLS = 2;
  const GAP = 8;
  const colWidth = size.width > 0 ? (size.width - GAP * (COLS - 1)) / COLS : 160;
  const rowHeight = colWidth * (2 / 5) + 70; // aspect 5/2 + meta block

  const Cell = ({ columnIndex, rowIndex, style }: GridChildComponentProps) => {
    const idx = rowIndex * COLS + columnIndex;
    const skin = skins[idx];
    if (!skin) return null;

    return (
      <div
        style={{
          ...style,
          left: Number(style.left) + columnIndex * GAP,
          top: Number(style.top) + rowIndex * GAP,
          width: colWidth,
          height: rowHeight - GAP,
        }}
      >
        <SkinCard
          skin={skin}
          isFavorite={favorites.has(skin.uuid)}
          onToggleFav={onToggle}
          compact
        />
      </div>
    );
  };

  return (
    <div ref={wrapRef} className="w-full">
      {size.width > 0 && (
        <Grid
          columnCount={COLS}
          rowCount={Math.ceil(skins.length / COLS)}
          columnWidth={colWidth + GAP}
          rowHeight={rowHeight}
          width={size.width}
          height={size.height}
          overscanRowCount={3}
        >
          {Cell}
        </Grid>
      )}
    </div>
  );
}
